import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import type { ContentRow } from "~/components/Document/tables/types";
import { renderMarkdownToNode } from "~/hooks/useMarkdownRenderer";
import type { Document as AppDocument } from "~/interface/document";
import styles from "./index.module.scss";

interface HistoryAppDesignProps {
  formData: AppDocument[];
}

const renderMarkdown = renderMarkdownToNode;

const HistoryAppDesign = ({ formData }: HistoryAppDesignProps) => {
  const rawContent = formData[0]?.content;
  const [selectedTab, setSelectedTab] = useState(0);

  const { parsedContent, isMarkdown, markdownContent } = useMemo(() => {
    try {
      const parsed = JSON.parse(rawContent as string);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const tableContainer = parsed["テーブル一覧"] ?? parsed["tables"];
        if (tableContainer && typeof tableContainer === "object") {
          return { parsedContent: parsed, isMarkdown: false, markdownContent: "" };
        }
      }
      // Markdown形式の場合
      return {
        parsedContent: null,
        isMarkdown: true,
        markdownContent: rawContent as string,
      };
    } catch {
      // JSONパースに失敗した場合もMarkdown扱い
      return {
        parsedContent: null,
        isMarkdown: true,
        markdownContent: rawContent as string,
      };
    }
  }, [rawContent]);

  const tableKeys = useMemo(
    () => ["アプリテーブル", "アプリ間連携テーブル", "要求機能テーブル"],
    [],
  );

  const tabDisplayLabels: Record<string, string> = useMemo(
    () => ({
      アプリテーブル: "作成するアプリ",
      アプリ間連携テーブル: "アプリ間の連携",
      要求機能テーブル: "アプリ毎の機能",
    }),
    [],
  );

  const labelMap: Record<string, Record<string, string>> = useMemo(
    () => ({
      アプリテーブル: {
        app_id: "アプリID",
        app_name: "アプリ名",
        description: "説明",
      },
      アプリ間連携テーブル: {
        connector_id: "連携ID",
        sid: "データ元アプリ",
        tid: "連携先アプリ",
        relationship: "連携方法",
        keyfield: "キーフィールド",
        referencefield: "参照フィールド",
      },
      要求機能テーブル: {
        app_id: "アプリ名",
        rid: "機能ID",
        name: "機能",
        description: "説明",
        how: "実装方法",
      },
    }),
    [],
  );

  const { columns, data } = useMemo(() => {
    const tables = parsedContent?.["テーブル一覧"] ?? parsedContent?.["tables"];
    if (!tables) return { columns: [], data: [] };

    const key = tableKeys[selectedTab];
    const table = (tables as Record<string, { headers: string[]; rows: string[][] }>)[key!];

    if (!table?.headers || !table?.rows) return { columns: [], data: [] };

    // アプリテーブルからアプリID→アプリ名のマップを構築
    const appNameMap: Record<string, string> = {};
    const appTable = (tables as Record<string, { headers: string[]; rows: string[][] }>)[
      "アプリテーブル"
    ];
    if (appTable?.headers && appTable?.rows) {
      const appIdIdx = appTable.headers.indexOf("app_id");
      const appNameIdx = appTable.headers.indexOf("app_name");
      if (appIdIdx >= 0 && appNameIdx >= 0) {
        for (const row of appTable.rows) {
          const id = row[appIdIdx] ?? "";
          const name = row[appNameIdx] ?? "";
          if (id) appNameMap[id] = name;
        }
      }
    }

    // sid/tid/app_id カラムの値をアプリ名に変換するヘッダーのセット
    // アプリテーブル自体の app_id は変換しない（IDとして表示する）
    const appIdColumns: Set<string> =
      key === "アプリテーブル" ? new Set() : new Set(["sid", "tid", "app_id"]);

    const columnHelper = createColumnHelper<ContentRow>();
    const columns = table.headers.map((header: string) =>
      columnHelper.accessor(header, {
        id: header,
        header: labelMap[key!]?.[header] ?? header,
        cell: (info) => {
          const value = info.getValue();
          if (appIdColumns.has(header) && typeof value === "string" && appNameMap[value]) {
            return appNameMap[value];
          }
          return value;
        },
      }),
    );

    const data = table.rows.map((row: string[]) => {
      const rowObj: ContentRow = {};
      table.headers.forEach((key: string, idx: number) => {
        rowObj[key] = row[idx] ?? "";
      });
      return rowObj;
    });

    return { columns, data };
  }, [parsedContent, selectedTab, labelMap, tableKeys]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isMarkdown) {
    return (
      <div className={`${styles["wrapper"]} ${styles["document-content"]}`}>
        <div className={styles["markdown-content"]}>{renderMarkdown(markdownContent || "")}</div>
      </div>
    );
  }

  if (!(parsedContent?.["テーブル一覧"] ?? parsedContent?.["tables"])) {
    return <div>データがありません</div>;
  }

  return (
    <div className={styles["wrapper"]}>
      <Tabs selectedIndex={selectedTab} onSelect={setSelectedTab}>
        <TabList className={styles["tab-list"]}>
          {tableKeys.map((key) => (
            <Tab
              key={key}
              className={styles["tab-list-name"]}
              selectedClassName={styles["tab-list-name--selected"]}
            >
              {tabDisplayLabels[key] ?? key}
            </Tab>
          ))}
        </TabList>

        {tableKeys.map((label) => (
          <TabPanel key={label}>
            <div className={styles["table-wrapper"]}>
              <table className={styles["table"]}>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th key={header.id}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

export default HistoryAppDesign;
